import { FC, ReactNode, useState } from "react";

import { Box, BoxProps } from "@mui/material";

import { mergeSx } from "@/common/helpers/mergeSx";
import { useInteractiveElementEvents } from "@/common/hooks/useInteractiveElementEvents";

type FlipCardProps = Omit<BoxProps, "onClick"> & {
  front: ReactNode;
  back: ReactNode;
};

export const FlipCard: FC<FlipCardProps> = ({ front, back, sx, ...rest }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const toggleFlip = () => setIsFlipped(!isFlipped);
  const { handleKeyDown, handleKeyUp } = useInteractiveElementEvents<HTMLDivElement>({ onClick: toggleFlip });

  return (
    <Box
      {...rest}
      role="button"
      tabIndex={0}
      aria-pressed={isFlipped}
      onClick={toggleFlip}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      sx={mergeSx(
        (theme) => ({
          perspective: "1000px",
          position: "relative",
          width: "100%",
          cursor: "pointer",
          borderRadius: theme.radius(1),
          outlineColor: "transparent",
          ["&:focus-visible"]: {
            outline: `3px solid ${theme.palette.formsComponents?.global.focusOutlineColor}`,
          },
        }),
        sx
      )}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
          transition: "transform 0.4s",
          transformStyle: "preserve-3d",
          transform: isFlipped ? "rotateX(180deg)" : "none",
        }}
      >
        {/* Front Side */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            backfaceVisibility: "hidden",
            zIndex: 0,
          }}
          aria-hidden={isFlipped}
        >
          {front}
        </Box>
        {/* Back Side */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backfaceVisibility: "hidden",
            transform: "rotateX(180deg)",
            zIndex: 1,
          }}
          aria-hidden={!isFlipped}
        >
          {back}
        </Box>
      </Box>
    </Box>
  );
};
